import { useState, useCallback } from 'react';
import { useEventListener } from './useEventBus';

interface UseSmartRefreshOptions {
  onRefresh: () => Promise<void>;
  autoRefreshOnEvents?: boolean;
  showToastOnRefresh?: boolean;
}

interface UseSmartRefreshReturn {
  isRefreshing: boolean;
  refresh: () => Promise<void>;
  refreshCount: number;
}

/**
 * Hook que gerencia refresh inteligente baseado em eventos
 */
export const useSmartRefresh = ({ 
  onRefresh, 
  autoRefreshOnEvents = true,
  showToastOnRefresh = false 
}: UseSmartRefreshOptions): UseSmartRefreshReturn => {
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [refreshCount, setRefreshCount] = useState(0);

  const refresh = useCallback(async () => {
    if (isRefreshing) return;
    
    try {
      setIsRefreshing(true);
      await onRefresh();
      setRefreshCount(prev => prev + 1);
      
      if (showToastOnRefresh && typeof window !== 'undefined') {
        const { toast } = await import('../components/Toast');
        toast.success('Dados atualizados!', { duration: 2000 });
      }
      
    } catch (error) {
      console.error('Erro ao atualizar dados:', error);
      
      if (showToastOnRefresh && typeof window !== 'undefined') {
        const { toast } = await import('../components/Toast');
        toast.error('Erro ao atualizar dados', { duration: 3000 });
      }
      
      throw error;
    } finally {
      setIsRefreshing(false);
    }
  }, [onRefresh, isRefreshing, showToastOnRefresh]);

  // Auto-refresh em eventos importantes se habilitado
  useEventListener('sector-changed', () => {
    if (autoRefreshOnEvents) {
      refresh();
    }
  }, [autoRefreshOnEvents]);

  useEventListener('demand-created', () => {
    if (autoRefreshOnEvents) {
      refresh();
    }
  }, [autoRefreshOnEvents]);

  useEventListener('demand-status-changed', () => {
    if (autoRefreshOnEvents) {
      refresh();
    }
  }, [autoRefreshOnEvents]);

  useEventListener('demand-assigned', () => {
    if (autoRefreshOnEvents) {
      refresh();
    }
  }, [autoRefreshOnEvents]);

  return {
    isRefreshing,
    refresh,
    refreshCount
  };
};